import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CloudIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ServerStackIcon,
} from "@heroicons/react/24/outline";
import NotificationCenter from "./ui/NotificationCenter";
const DashboardOverview = ({
  dashboardStats = {},
  assetsByProvider = {},
  findings = [],
  assets = [],
  remediationPlans = [],
  loadDashboard,
  onViewAllFindings,
  scans = [],
  scansLoading = false,
  connectorOptions = [],
  selectedConnectorId = "",
  onSelectedConnectorChange,
  onTriggerScan,
  onRescan,
  scanTriggerLoading = false,
  rescanLoadingId = null,
  canTriggerScans = false,
}) => {
  const navigate = useNavigate();

  /* ---------------- Totals ----------------
     `assets`/`findings` here are the paginated Asset Inventory /
     Findings list props (max page_size items), NOT the full count -
     using their .length anywhere on this dashboard undercounts
     everything once there's more than one page of data. Always
     prefer the backend's real totals (dashboard_stats), only
     falling back to the paginated array length if dashboardStats
     hasn't loaded yet. */

  const totalAssetsCount = dashboardStats.total_assets ?? assets.length;
  const totalFindingsCount = dashboardStats.total_findings ?? findings.length;

  /* ---------------- Severity Counts ----------------
     Same paginated-vs-total issue as totalAssetsCount/totalFindingsCount -
     prefer the backend's severity_breakdown (computed over ALL scoped
     findings in dashboard_stats), only falling back to counting the
     paginated `findings` prop client-side if it isn't available yet. */

  const severityBreakdown = dashboardStats?.severity_breakdown;

  const critical = severityBreakdown
    ? (severityBreakdown.critical ?? 0)
    : findings.filter((f) => (f.severity || "").toLowerCase() === "critical")
        .length;

  const high = severityBreakdown
    ? (severityBreakdown.high ?? 0)
    : findings.filter((f) => (f.severity || "").toLowerCase() === "high")
        .length;

  const medium = severityBreakdown
    ? (severityBreakdown.medium ?? 0)
    : findings.filter((f) => (f.severity || "").toLowerCase() === "medium")
        .length;

  const low = severityBreakdown
    ? (severityBreakdown.low ?? 0)
    : findings.filter((f) => (f.severity || "").toLowerCase() === "low").length;

  /* ---------------- Compliance Score ---------------- */

  const complianceScore = useMemo(() => {
    if (dashboardStats?.security_score !== undefined) {
      return dashboardStats.security_score;
    }

    if (!findings.length) return 100;

    const deduction = critical * 6 + high * 3 + medium;

    return Math.max(0, 100 - deduction);
  }, [dashboardStats?.security_score, findings, critical, high, medium]);

  /* ---------------- Compliance by Provider (real data) ----------------
     dashboard_stats' compliance_scores = { aws, azure, gcp, overall }
     (ComplianceService.get_provider_scores) - the only real
     compliance-breakdown data currently exposed by the dashboard
     endpoint. No genuine per-framework (NIST/ISO/PCI) figures exist
     there, so nothing framework-level is shown here. */

  const complianceProviderScores = useMemo(() => {
    const scores = dashboardStats?.compliance_scores;
    if (!scores || typeof scores !== "object") return [];

    const labels = {
      overall: "Overall",
      aws: "AWS",
      azure: "Azure",
      gcp: "GCP",
    };

    return Object.entries(scores)
      .filter(([, value]) => value !== null && value !== undefined)
      .map(([key, value]) => ({
        label: labels[key] || key,
        score: Math.round(Number(value) || 0),
      }));
  }, [dashboardStats]);

  /* ---------------- Recent Activity (real, derived from scans) ---------------- */

  const recentActivity = useMemo(() => {
    const statusColor = {
      completed: "bg-green-500",
      failed: "bg-red-500",
      running: "bg-blue-500",
    };
    return [...scans]
      .sort((a, b) => new Date(b.started_at || 0) - new Date(a.started_at || 0))
      .slice(0, 5)
      .map((scan) => {
        const when = scan.completed_at || scan.started_at;
        const whenLabel = when ? new Date(when).toLocaleString() : "";
        return {
          color: statusColor[scan.status] || "bg-gray-400",
          title: `Scan ${scan.status || "unknown"} - ${scan.cloud_provider || "cloud"}`,
          subtitle: whenLabel || "Timestamp unavailable",
        };
      });
  }, [scans]);

  /* ---------------- Notification panel extras ----------------
     Recent Findings / Recent Activity used to be their own separate
     dashboard sections - now folded into the Notifications panel as
     plain client-side display items (never sent through the backend
     notifications API, never persisted - `_local: true` tells
     NotificationCenter to just navigate on click instead of calling
     markReadApi). Source data is unchanged (same `findings`/`scans`
     props), only where it's rendered changed. */

  const findingNotificationItems = useMemo(() => {
    return findings.slice(0, 5).map((finding, index) => ({
      id: `finding-${finding.id || finding.finding_id || index}`,
      _local: true,
      is_read: true,
      category: "Findings",
      notification_type:
        (finding.severity || "").toLowerCase() === "critical"
          ? "error"
          : (finding.severity || "").toLowerCase() === "high"
            ? "warning"
            : "info",
      message: `${finding.control_name || finding.control_id || "Cloud Resource"} - ${(finding.severity || "Unknown").toUpperCase()} (${finding.status || "Open"})`,
      created_at: finding.detected_at || finding.created_at,
      reference_url: "/ai-cspm?tab=findings",
    }));
  }, [findings]);

  const activityNotificationItems = useMemo(() => {
    return recentActivity.map((activity, index) => ({
      id: `activity-${index}`,
      _local: true,
      is_read: true,
      category: "Activity",
      notification_type: "info",
      message: `${activity.title} - ${activity.subtitle}`,
      created_at: null,
      reference_url: "/ai-cspm?tab=dashboard",
    }));
  }, [recentActivity]);

  const notificationExtraItems = useMemo(
    () => [...findingNotificationItems, ...activityNotificationItems],
    [findingNotificationItems, activityNotificationItems],
  );

  /* ---------------- KPI Cards ---------------- */

  const cards = [
    {
      title: "Cloud Assets",
      value: totalAssetsCount,
      subtitle: "Connected Resources",
      icon: CloudIcon,
      color: "bg-blue-50 text-blue-600",
      tab: "assets",
    },
    {
      title: "Compliance",
      value: `${complianceScore}%`,
      subtitle: "Overall Security",
      icon: ShieldCheckIcon,
      color: "bg-green-50 text-green-600",
      tab: "dashboard",
    },
    {
      title: "Open Findings",
      value: totalFindingsCount,
      subtitle: "Needs Attention",
      icon: ExclamationTriangleIcon,
      color: "bg-red-50 text-red-600",
      tab: "findings",
    },
    {
      title: "Remediation",
      value: dashboardStats.total_remediation ?? remediationPlans.length,
      subtitle: "Pending Actions",
      icon: CheckCircleIcon,
      color: "bg-purple-50 text-purple-600",
      tab: "remediation",
    },
    {
      title: "Total Scans",
      value: dashboardStats.total_scans ?? 0,
      subtitle: "Scans Run",
      icon: ServerStackIcon,
      color: "bg-indigo-50 text-indigo-600",
      tab: "dashboard",
    },
    {
      title: "Rules",
      value: dashboardStats.total_checks ?? 0,
      subtitle: "Security Checks",
      icon: ShieldCheckIcon,
      color: "bg-teal-50 text-teal-600",
      tab: "rules",
    },
  ];

  return (
    <div className="space-y-4">
      {/* ================= KPI CARDS + NOTIFICATIONS ================= */}

      <div className="flex flex-col lg:flex-row gap-4 items-stretch">
        <div className="w-full lg:w-[28rem] flex-shrink-0 h-[380px]">
          <NotificationCenter
            module="ai_cspm"
            title="CSPM Notifications"
            extraItems={notificationExtraItems}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 flex-1">
          {cards.map((card, index) => {
            const Icon = card.icon;

            return (
              <motion.div
                key={card.title}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.08 }}
                onClick={() => navigate(`/ai-cspm?tab=${card.tab}`)}
                role="button"
                tabIndex={0}
                className="bg-white rounded-xl border border-gray-200 shadow-lg p-3 hover:shadow-xl transition-all cursor-pointer"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-500">
                      {card.title}
                    </p>

                    <h2 className="mt-1 text-3xl font-bold text-gray-900">
                      {card.value}
                    </h2>

                    <p className="text-xs text-gray-500">{card.subtitle}</p>
                  </div>

                  <div
                    className={`flex h-12 w-12 items-center justify-center rounded-lg ${card.color}`}
                  >
                    <Icon className="h-6 w-6" />
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* ================= SECURITY OVERVIEW ================= */}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Security Score */}

        <div className="bg-white rounded-xl border border-gray-200 shadow-lg p-4 h-[380px]">
          <h2 className="text-lg font-bold text-gray-900">Security Score</h2>

          <div className="flex justify-center py-6">
            <div className="relative flex h-36 w-36 items-center justify-center rounded-full border-[12px] border-blue-100">
              <div className="text-center">
                <h1 className="text-4xl font-bold text-blue-600">
                  {complianceScore}
                </h1>

                <p className="text-sm text-gray-500">Score</p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Secure Resources</span>

              <span className="font-semibold text-green-600">
                {Math.max(0, totalAssetsCount - totalFindingsCount)}
              </span>
            </div>

            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Misconfigured</span>

              <span className="font-semibold text-orange-600">
                {totalFindingsCount}
              </span>
            </div>

            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Critical Risks</span>

              <span className="font-semibold text-red-600">{critical}</span>
            </div>
          </div>
        </div>

        {/* Findings by Severity */}

        <div className="bg-white rounded-xl border border-gray-200 shadow-lg p-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold text-gray-900">
              Findings by Severity
            </h2>

            <ExclamationTriangleIcon className="h-5 w-5 text-orange-500" />
          </div>

          <div className="mt-5 space-y-4">
            {[
              {
                label: "Critical",
                value: critical,
                color: "bg-red-500",
              },
              {
                label: "High",
                value: high,
                color: "bg-orange-500",
              },
              {
                label: "Medium",
                value: medium,
                color: "bg-yellow-400",
              },
              {
                label: "Low",
                value: low,
                color: "bg-blue-500",
              },
            ].map((item) => (
              <div key={item.label}>
                <div className="flex justify-between mb-2 text-sm">
                  <span className="text-gray-700">{item.label}</span>

                  <span className="font-semibold">{item.value}</span>
                </div>

                <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{
                      width: totalFindingsCount
                        ? `${(item.value / totalFindingsCount) * 100}%`
                        : "0%",
                    }}
                    transition={{ duration: 0.8 }}
                    className={`${item.color} h-full rounded-full`}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Compliance Status - real data only.
            dashboard_stats' compliance_scores is a PROVIDER breakdown
            (aws/azure/gcp/overall), not per-framework - there is no
            genuine per-framework (NIST/ISO 27001/PCI DSS) breakdown
            wired to the dashboard endpoint, so rather than inventing
            fake offsets (-4/-8/-6) this shows the real provider scores
            that dashboard_stats actually returns. */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-lg p-4">
          <h2 className="text-lg font-bold text-gray-900">Compliance Status</h2>

          <div className="mt-5 space-y-4">
            {complianceProviderScores.length > 0 ? (
              complianceProviderScores.map((entry) => (
                <div key={entry.label}>
                  <div className="flex justify-between mb-2 text-sm">
                    <span className="text-gray-700">{entry.label}</span>

                    <span className="font-semibold text-blue-600">
                      {entry.score}%
                    </span>
                  </div>

                  <div className="h-2 rounded-full bg-gray-200">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{
                        width: `${entry.score}%`,
                      }}
                      transition={{ duration: 0.8 }}
                      className="h-full rounded-full bg-blue-600"
                    />
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500">
                Per-provider compliance scores are not available yet.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* ================= CLOUD OVERVIEW ================= */}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Assets by Cloud Provider */}

        <div className="bg-white rounded-xl border border-gray-200 shadow-lg p-4">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-bold text-gray-900">
              Assets by Cloud Provider
            </h2>

            <CloudIcon className="w-5 h-5 text-blue-600" />
          </div>

          <div className="space-y-5">
            {[
              {
                provider: "AWS",
                count: assetsByProvider.aws ?? 0,
                color: "bg-orange-500",
              },
              {
                provider: "Azure",
                count: assetsByProvider.azure ?? 0,
                color: "bg-blue-500",
              },
              {
                provider: "GCP",
                count: assetsByProvider.gcp ?? 0,
                color: "bg-green-500",
              },
            ].map((cloud) => {
              const total = assetsByProvider.total || 1;
              const percentage = (cloud.count / total) * 100;

              return (
                <div key={cloud.provider}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">
                      {cloud.provider}
                    </span>

                    <span className="text-sm font-semibold text-gray-900">
                      {cloud.count}
                    </span>
                  </div>

                  <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${percentage}%` }}
                      transition={{ duration: 0.8 }}
                      className={`h-full rounded-full ${cloud.color}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Cloud Overview */}

        <div className="bg-white rounded-xl border border-gray-200 shadow-lg p-4">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-bold text-gray-900">Cloud Overview</h2>

            <ServerStackIcon className="w-5 h-5 text-blue-600" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide">
                Connected Assets
              </p>

              <h2 className="mt-2 text-3xl font-bold text-gray-900">
                {totalAssetsCount}
              </h2>
            </div>

            <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide">
                Open Findings
              </p>

              <h2 className="mt-2 text-3xl font-bold text-red-600">
                {totalFindingsCount}
              </h2>
            </div>

            <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide">
                Critical Risks
              </p>

              <h2 className="mt-2 text-3xl font-bold text-orange-600">
                {critical}
              </h2>
            </div>

            <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide">
                Compliance
              </p>

              <h2 className="mt-2 text-3xl font-bold text-green-600">
                {complianceScore}%
              </h2>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default DashboardOverview;
